import { clsx } from 'clsx';
import React from 'react';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?:
    | 'primary'
    | 'accent'
    | 'secondary'
    | 'success'
    | 'warning'
    | 'error'
    | 'outline';
  size?: 'sm' | 'md' | 'lg';
  loading?: boolean;
  children: React.ReactNode;
}

const Button: React.FC<ButtonProps> = ({
  variant,
  size = 'md',
  loading = false,
  disabled,
  className,
  children,
  ...props
}) => {
  const baseClasses =
    'btn inline-flex items-center justify-center font-medium transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed';

  const variantClasses = {
    primary:
      'bg-primary-600 dark:bg-primary-700 text-white hover:bg-primary-700 dark:hover:bg-primary-600 focus:ring-primary-500',
    accent:
      'border border-primary-400 bg-primary-50 text-primary-700 hover:bg-primary-100 dark:border-primary-500 dark:bg-primary-900/30 dark:text-primary-300 dark:hover:bg-primary-900/40 focus:ring-primary-500',
    secondary:
      'bg-secondary-600 dark:bg-secondary-700 text-white hover:bg-secondary-700 dark:hover:bg-secondary-600 focus:ring-secondary-500',
    success:
      'bg-success-600 dark:bg-success-700 text-white hover:bg-success-700 dark:hover:bg-success-600 focus:ring-success-500',
    warning:
      'bg-warning-600 dark:bg-warning-700 text-white hover:bg-warning-700 dark:hover:bg-warning-600 focus:ring-warning-500',
    error:
      'bg-error-600 dark:bg-error-700 text-white hover:bg-error-700 dark:hover:bg-error-600 focus:ring-error-500',
    outline:
      'border border-light-300 dark:border-graphite-600 text-deepTechBlue-500 dark:text-light-300 bg-white dark:bg-graphite-800 hover:bg-light-50 dark:hover:bg-graphite-700 focus:ring-primary-500',
  };

  const sizeClasses = {
    sm: 'h-8 px-3 text-sm',
    md: 'h-10 px-4 text-sm',
    lg: 'h-12 px-6 text-base',
  };
  const resolvedVariant = variant ?? 'accent';

  return (
    <button
      className={clsx(
        baseClasses,
        variantClasses[resolvedVariant],
        sizeClasses[size],
        className
      )}
      disabled={disabled || loading}
      {...props}
    >
      {loading && (
        <svg
          className="animate-spin -ml-1 mr-2 h-4 w-4"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      )}
      {children}
    </button>
  );
};

export default Button;
